import React, { useState, useEffect, useCallback, useMemo } from 'react';
import './referral.css';
import { getLocalItem } from '../../../../shared/utils/config/localStorage';
import ModernHeader from '../../../../components/layout/Headers/ModernHeader';
import {
  getMyBonusHistory,
  getReferralAmount,
} from '../../../../shared/actions/referralAction';
import ReferralCard from '../../../../features/cards/ReferralCard';
import { renderPaginationButtons } from '../../../../shared/utils/paginationUtil';
import { formatAmountWithTwoDecimalPlaces } from '../../../../shared/utils/function';
import { reportError } from '../../../../shared/sentry';

const HISTORY_LIMIT = 5;
const COPY_FEEDBACK_DURATION = 2000;

const normalizeHistoryData = (data) => {
  if (Array.isArray(data)) {
    return data;
  }
  return Array.isArray(data?.data) ? data.data : [];
};

const Referral = () => {
  const [referralLink, setReferralLink] = useState('');
  const [copied, setCopied] = useState(false);
  const [bonusHistory, setBonusHistory] = useState([]);
  const [referralAmount, setReferralAmount] = useState(0);
  const [recordsTotal, setRecordsTotal] = useState(0);
  const [isStatsLoading, setIsStatsLoading] = useState(true);
  const [isHistoryLoading, setIsHistoryLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isHowItWorksExpanded, setIsHowItWorksExpanded] = useState(true);

  const origin = useMemo(
    () => (typeof window !== 'undefined' && window.location?.origin ? window.location.origin : ''),
    []
  );

  const generateReferralLink = useCallback(() => {
    const username = getLocalItem('username');
    if (!username) return '';
    return `${origin}/referral/${username}`;
  }, [origin]);

  const loadReferralAmount = useCallback(async () => {
    setIsStatsLoading(true);
    try {
      const amountRes = await getReferralAmount();
      if (amountRes?.success) {
        setReferralAmount(amountRes.data?.amount || 0);
      }
    } catch (error) {
      reportError(error, 'Referral load amount');
    } finally {
      setIsStatsLoading(false);
    }
  }, []);

  const loadReferralHistory = useCallback(async () => {
    setIsHistoryLoading(true);
    try {
      const skip = currentPage - 1;
      const historyRes = await getMyBonusHistory({ limit: HISTORY_LIMIT, skip });

      if (historyRes?.success) {
        const historyData = normalizeHistoryData(historyRes.data);
        setBonusHistory(historyData);
        const totalRecords = historyRes.data?.total_records || 0;
        const totalPagesCount = historyRes.data?.total_pages || Math.ceil(totalRecords / HISTORY_LIMIT);
        setRecordsTotal(totalRecords);
        setTotalPages(totalPagesCount);
      }
    } catch (error) {
      reportError(error, 'Referral load history');
    } finally {
      setIsHistoryLoading(false);
    }
  }, [currentPage]);

  const handleCopyLink = useCallback(async () => {
    if (!referralLink || !navigator?.clipboard?.writeText) return;

    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      setTimeout(() => setCopied(false), COPY_FEEDBACK_DURATION);
    } catch (error) {
      reportError(error, 'Referral copy link');
    }
  }, [referralLink]);

  const formattedReferralAmount = useMemo(
    () => formatAmountWithTwoDecimalPlaces(referralAmount),
    [referralAmount]
  );

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  useEffect(() => {
    const link = generateReferralLink();
    setReferralLink(link);
  }, [generateReferralLink]);

  useEffect(() => {
    loadReferralAmount();
  }, [loadReferralAmount]);

  useEffect(() => {
    loadReferralHistory();
  }, [loadReferralHistory]);

  return (
    <div className="modern-earnings-payout-page">
      <ModernHeader
        customTitle="Referrals"
        showButtonBorder={false}
        hideSearchIcon
        showAvatar={false}
        showBackButton
      />

      <div className="modern-referral-grid mt-4">
        <div className="modern-referral-card">
          <div className="referral_doller_icon">
            <i className="fa-solid fa-sack-dollar"></i>
          </div>
          <p>Total Referral Earnings</p>
          {isStatsLoading ? (
            <div className="modern-referral-stats-loading">
              <div className="modern-referral-stats-spinner"></div>
              <p className="modern-referral-stats-loading-text">Loading...</p>
            </div>
          ) : (
            <div className="modern-referral-stats-container modern-referral-fade-in">
              <div className="modern-referral-earnings-circle">
                <h2>${formattedReferralAmount}</h2>
                {recordsTotal > 0 && (
                  <div className='cm_formatted_records_total'>
                    From {recordsTotal} active referrals
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="modern-referral-card modern-referral-share-card">
          <h2 className="modern-referral-card-title">
            <i className='fa-solid fa-link'></i> Your Referral Link
          </h2>
          <div className="modern-referral-link-section">
            <div className="modern-referral-link-container">
              <div className="modern-referral-link-text">
                {referralLink}
              </div>
              <button
                className="modern-referral-copy-btn"
                onClick={handleCopyLink}
                type="button"
              >
                <i className='fa-solid fa-copy'></i> Copy link
              </button>
            </div>
            {copied && (
              <div className='text-center'>
                <span className="modern-referral-copied-text">
                  <i>Copied!</i>
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="modern-referral-card cm_how_its_work">
          <div
            className='cm_how_its_work_head'
            onClick={() => setIsHowItWorksExpanded(!isHowItWorksExpanded)}
          >
            <h2 className="modern-referral-card-title">
              <i className='fa-solid fa-lightbulb'></i> How It Works
            </h2>
            <i
              className={`fa-solid fa-chevron-down ${isHowItWorksExpanded ? 'expanded' : ''}`}
            ></i>
          </div>

          {isHowItWorksExpanded && (
            <div className="cm_how_its_work_body">
              <div>
                <span>1</span>
                <h4>Share your link</h4>
                <p>Click the copy link button to get a copy of your referral link</p>
              </div>
              <div>
                <span>2</span>
                <h4>They sign up using your link</h4>
                <p>Creators will sign up through the link you shared</p>
              </div>
              <div>
                <span>3</span>
                <h4>They earn, you earn</h4>
                <p>You receive a 5% lifetime commission</p>
              </div>
            </div>
          )}
        </div>

        <div className="modern-referral-card cm_how_its_work">
          <div className='cm_how_its_work_head'>
            <h2 className="modern-referral-card-title">
              <i className='fa-solid fa-users'></i> Your Referrals
            </h2>
          </div>

          <div className='your_reff_body'>
            {isHistoryLoading ? (
              <div className="modern-referral-history-loading">
                <div className="modern-referral-history-spinner"></div>
                <p className="modern-referral-history-loading-text">Loading history...</p>
              </div>
            ) : bonusHistory?.length > 0 ? (
              bonusHistory.map((item, index) => (
                <ReferralCard key={`referral-${item?.id ?? index}`} item={item} index={index} />
              ))
            ) : (
              <p className="modern-referral-no-history modern-referral-fade-in">
                You have no referral history.
              </p>
            )}
          </div>

          {!isHistoryLoading && totalPages > 1 && (
            <div className="modern-referral-pagination">
              {renderPaginationButtons(currentPage, totalPages, handlePageChange)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Referral;
